import { useTranslation } from "react-i18next";
import { memo, useCallback } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cn } from "@/utils/cn";
import { useCanvasActions } from "../canvas-context";
import { useLongPress } from "../use-long-press";
import { usePointerSpotlight } from "../use-pointer-spotlight";
import { SIZE_LABELS } from "../grid.constants";
import { allowedSizes, isMaxSize, isMinSize, spanForSize } from "../grid.utils";
import type {
  IWidgetDefinition,
  IWidgetPlacement,
  TResizeAxis,
  TWidgetSizeKey,
} from "../types";
import { ResizeHandles } from "./ResizeHandles";
import { WidgetCard } from "./WidgetCard";
import { WidgetChrome } from "./WidgetChrome";

interface WidgetFrameProps {
  placement: IWidgetPlacement;
  definition: IWidgetDefinition;
  columns: number;
  isEditing: boolean;
  /**
   * Size the pointer is currently snapped to during a resize. Applied to the card
   * so the widget grows and the grid reflows live — an outline-only preview leaves
   * the user guessing what else will move.
   */
  previewSize: TWidgetSizeKey | null;
  /** Which handle is being dragged, for handle highlighting. */
  resizeAxis: TResizeAxis | null;
  /** True for the widget that was just added, to pull the eye to it once. */
  isArriving: boolean;
  /** Stagger index for the entrance animation, and the jiggle's phase offset. */
  index: number;
  /** Press-and-hold on the card opens customize mode, home-screen style. */
  onLongPress: () => void;
}

/**
 * One cell of the canvas: grid placement, drag wiring, and the customize chrome.
 *
 * Every prop is a primitive or a stable object identity, and the actions come
 * from context rather than from per-item closures, so the memo below genuinely
 * bites: dragging one widget re-renders that widget, not the other nine.
 *
 * While a widget is being dragged its real card lives in the drag overlay, so the
 * frame renders the dashed slot instead. That slot is also the insertion
 * indicator — as the sortable order shifts under the pointer, the hole moves to
 * the index the drop will use, which is a far more direct answer to "where will
 * this land" than watching neighbours slide.
 */
const WidgetFrameComponent = ({
  placement,
  definition,
  columns,
  isEditing,
  previewSize,
  resizeAxis,
  isArriving,
  index,
  onLongPress,
}: WidgetFrameProps) => {
  const { t } = useTranslation(["common", "company"]);
  const actions = useCanvasActions();
  // Only while reading. Once the canvas is in customize mode the pointer belongs
  // to dnd-kit, and a second gesture competing for it would swallow drags.
  const { isPressing, handlers: longPressHandlers } = useLongPress({
    onLongPress,
    enabled: !isEditing,
  });
  // Destructured rather than kept as one object: the object itself is a new
  // literal every render, and depending on it below would churn attachRef's
  // identity every render even though the three functions inside it are stable.
  const {
    attach: attachSpotlight,
    track: trackSpotlight,
    clear: clearSpotlight,
  } = usePointerSpotlight();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: placement.instanceId, disabled: !isEditing });

  // Reads on top of the long-press handlers rather than replacing them: the
  // press-to-jiggle gesture and the cursor-tracked glare share the same pointer,
  // and both need every move. Only bound in read mode — the glare's custom
  // properties never get set while editing, so `.widget-card::after` just falls
  // back to `transparent` there for free, no separate CSS scoping needed.
  const readModeHandlers = {
    ...longPressHandlers,
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      longPressHandlers.onPointerMove(event);
      trackSpotlight(event);
    },
    onPointerLeave: () => {
      longPressHandlers.onPointerLeave();
      clearSpotlight();
    },
  };

  const attachRef = useCallback(
    (element: HTMLDivElement | null) => {
      setNodeRef(element);
      attachSpotlight(element);
    },
    [setNodeRef, attachSpotlight],
  );

  const effectiveSize = previewSize ?? placement.size;
  const span = spanForSize(effectiveSize, columns);
  const isResizing = previewSize !== null;
  const sizes = allowedSizes(definition);

  const handleRemove = useCallback(
    () => actions.remove(placement.instanceId),
    [actions, placement.instanceId],
  );

  const handleResize = useCallback(
    (size: TWidgetSizeKey) => actions.resize(placement.instanceId, size),
    [actions, placement.instanceId],
  );

  const handleStep = useCallback(
    (direction: 1 | -1) =>
      actions.step(placement.instanceId, definition, effectiveSize, direction),
    [actions, definition, effectiveSize, placement.instanceId],
  );

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>, axis: TResizeAxis) =>
      actions.beginResize(
        event,
        placement.instanceId,
        definition,
        placement.size,
        axis,
      ),
    [actions, definition, placement.instanceId, placement.size],
  );

  return (
    <div
      ref={attachRef}
      role="listitem"
      style={{
        gridColumn: `span ${span.w}`,
        gridRow: `span ${span.h}`,
        transform: CSS.Translate.toString(transform),
        transition,
        // Stagger only the first paint; later renders keep the delay but the
        // animation has already run, so it costs nothing.
        animationDelay: `${Math.min(index, 8) * 45}ms`,
        zIndex: isResizing ? 25 : undefined,
        // Names this card for the View Transitions API so the browser can tween
        // it from its old grid slot to its new one. Instance ids are unique and
        // are valid CSS custom-idents (they always start with the widget type,
        // which is lowercase letters). Dropped mid-drag: dnd-kit is transforming
        // this element itself, and a captured snapshot would fight it.
        viewTransitionName: isDragging ? undefined : placement.instanceId,
      }}
      className={cn(
        "group/card animate-widget-in widget-pressable relative min-h-0",
        isEditing && !isDragging && "widget-card-editable",
        isArriving && "widget-arriving rounded-widget",
        isResizing &&
          "ring-2 ring-ring ring-offset-2 ring-offset-transparent rounded-widget",
      )}
      // Pointer-drag from anywhere on the card while customizing; press-and-hold
      // anywhere on it while reading. The chrome and the handles stop propagation
      // so they stay clickable either way.
      {...(isEditing ? listeners : readModeHandlers)}
    >
      {isDragging ? (
        // The card is in the overlay; this is the gap it will drop back into.
        <div className="widget-slot widget-slot-live size-full" />
      ) : (
        // The jiggle lives on this wrapper, never on the node above: dnd-kit
        // writes its drag translate there as an inline style, and a CSS
        // animation's transform would outrank it and pin the card in place.
        <div
          className={cn(
            "h-full",
            isEditing && "widget-jiggle",
            isEditing && index % 2 === 1 && "widget-jiggle-alt",
            isPressing && "widget-pressing",
          )}
          style={
            isEditing
              ? // Negative delay starts each card mid-cycle, so ten of them read
                // as ten loose cards rather than one shaking page.
                { animationDelay: `-${(index % 5) * 60}ms` }
              : undefined
          }
        >
          <WidgetCard
            definition={definition}
            size={effectiveSize}
            isEditing={isEditing}
            className={cn(
              !isEditing && "widget-card-interactive",
              // Bodies are inert while arranging: a link inside a widget must not
              // swallow the start of a drag.
              isEditing && "[&_[data-widget-body]]:pointer-events-none",
            )}
          />
        </div>
      )}

      {isEditing && !isDragging && (
        <>
          <WidgetChrome
            widgetName={t(definition.name)}
            sizes={sizes}
            size={effectiveSize}
            activatorRef={setActivatorNodeRef}
            activatorProps={{ ...attributes, ...listeners }}
            onResize={handleResize}
            onRemove={handleRemove}
          />

          <ResizeHandles
            widgetName={t(definition.name)}
            sizeLabel={SIZE_LABELS[effectiveSize]}
            isMin={isMinSize(definition, effectiveSize)}
            isMax={isMaxSize(definition, effectiveSize)}
            activeAxis={resizeAxis}
            onPointerDown={handleResizePointerDown}
            onStep={handleStep}
          />

          {/* Snap readout — names the size the release will commit to. */}
          {isResizing && (
            <span className="pointer-events-none absolute end-2 bottom-2 z-30 rounded-full bg-primary px-2 py-0.5 text-widget-caption font-semibold tracking-[0.06em] text-primary-foreground uppercase">
              {SIZE_LABELS[effectiveSize]} · {span.w}×{span.h}
            </span>
          )}
        </>
      )}
    </div>
  );
};

export const WidgetFrame = memo(WidgetFrameComponent);
